<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading" :class="{ columns: isRecurring }">
      <div :class="{ 'column is-4': isRecurring }">
        <div
          :class="[
            'instant-quote-details-view--column-reverse',
            isRecurring ? 'instant-quote-details-view--service-header' : 'columns',
          ]"
        >
          <div :class="{ 'column is-9': !isRecurring }">
            <h1 class="has-text-weight-bold">{{ serviceName }}</h1>
          </div>
          <div v-if="!isRecurring" class="column is-3">
            <div class="instant-quote-details-view--address">
              <LsAddressCard :property="selectedProperty" />
            </div>
          </div>
          <div v-if="isRecurring">
            <h2>For {{ serviceHistory.schedule.property.street }}</h2>
          </div>
        </div>

        <div
          :class="{
            'columns instant-quote-details-view--details-wrapper': !isRecurring,
          }"
        >
          <div
            :class="
              isRecurring
                ? 'instant-quote-details-view--recurring-detail'
                : 'column is-9 ls-left-column'
            "
          >
            <div
              v-for="detail in details"
              :key="detail.title"
              :class="{ 'instant-quote-details-view--details': !isRecurring }"
            >
              <p class="has-text-weight-bold">{{ detail.title }}</p>
              <p
                test-id="details_values"
                :class="{ 'instant-quote-details-view--detail': !isRecurring }"
              >
                {{ detail.value }}
              </p>
            </div>
          </div>
          <div :class="{ 'column is-3 ls-right-column': !isRecurring }">
            <div v-if="images.length">
              <p class="instant-quote-details-view--photos-text">Photos</p>
              <div
                :class="
                  isRecurring
                    ? 'instant-quote-details-view--images-wrap'
                    : 'instant-quote-details-view--images'
                "
              >
                <LsImage
                  v-for="image in images"
                  :key="image.image_id"
                  class="instant-quote-details-view--image"
                  :src="image.image_url"
                  :thumb-src="image.image_url"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isRecurring" class="column is-8 is-offset-1" test-id="instant_quote_history">
        <h1 class="insant-quote-details-view--history-header">Service History</h1>
        <ServiceHistoryItem
          v-for="event in orderedEvents"
          :key="event.scheduleevent_id"
          :event="event"
        />
      </div>

      <div v-if="!isRecurring" class="columns instant-quote-details-view--price">
        <div class="column">
          <InstantQuotePriceAndSchedule
            :current-quote="schedule.instant_quote"
            :date="{
              next_starts_at: schedule.next_starts_at,
              next_ends_at: schedule.next_ends_at,
            }"
          />
          <InstantQuoteAuthorized :date-authorized="schedule.instant_quote.customer_accepted_at" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import {
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  SERVICE_HISTORY__GET_SCHEDULE,
} from '@/store/types';
import { LsAddressCard, LsImage } from '@/components/common';
import { lsFilters, lsRouterHelpers, lsScheduleHelpers } from 'ls-vue-common';
import InstantQuotePriceAndSchedule from '@/views/Authorized/OrderServicesView/OrderServicesFormView/components/InstantQuotePriceAndSchedule.vue';
import InstantQuoteAuthorized from '@/views/Authorized/OrderServicesView/OrderServicesFormView/components/InstantQuoteAuthorized.vue';
import ServiceHistoryItem from '@/views/Authorized/ServicesView/ServiceHistoryView/components/ServiceHistoryItem.vue';

export default {
  name: 'InstantQuoteDetailsView',
  components: {
    LsAddressCard,
    LsImage,
    InstantQuotePriceAndSchedule,
    InstantQuoteAuthorized,
    ServiceHistoryItem,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      schedule_id: lsRouterHelpers.getParam(this.$route, 'schedule_id'),
      isCheckingRouting: true,
    };
  },
  computed: {
    ...mapState(['schedules', 'properties', 'serviceHistory']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),

    isLoading() {
      return (
        this.properties.loadingStatus.isLoading ||
        _.isNil(this.selectedProperty) ||
        _.isNil(this.schedule) ||
        this.isCheckingRouting
      );
    },
    images() {
      const { images } = this.schedule.instant_quote[this.iqType];

      return images ? images : [];
    },
    details() {
      const cycle = _.get(this.schedule, 'cycle');

      return [
        {
          title: `Frequency`,
          value: lsFilters.frequency({ cycle }),
        },
        {
          title: `Crew`,
          value: _.get(this.schedule, 'contractor.name', 'TBD'),
        },
        ...this.schedule.instant_quote[this.iqType].copy.description,
      ];
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    schedule() {
      return this.schedules.schedulesById[this.schedule_id];
    },
    iqType() {
      return this.schedule.instant_quote.type;
    },
    serviceName() {
      return lsScheduleHelpers.getScheduleServiceName({ schedule: this.schedule });
    },
    isRecurring() {
      return this.schedule.cycle !== 0;
    },
    orderedEvents() {
      return _.orderBy(this.serviceHistory.schedule.scheduleevents, 'scheduleevent_id', 'desc');
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();
    await this[SERVICE_HISTORY__GET_SCHEDULE]({
      property_id: this.property_id,
      schedule_id: this.schedule_id,
    });
    if (_.isNil(this.selectedProperty) || _.isNil(this.schedule)) {
      return this.$router.push({
        name: 'services',
      });
    }
    if (this.schedule.type === 'manualquote') {
      return this.$router.push({
        name: 'quoteDetails',
      });
    }
    if (this.schedule.type !== 'instant_quote') {
      return this.$router.push({
        name: 'serviceHistory',
      });
    }
    this.isCheckingRouting = false;
  },
  methods: {
    ...mapActions([PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, SERVICE_HISTORY__GET_SCHEDULE]),
  },
};
</script>

<style lang="scss" scoped>
@media (max-width: $mobile-max-width) {
  .instant-quote-details-view--column-reverse {
    display: flex;
    flex-direction: column-reverse;
    height: 140px;
  }
  .instant-quote-details-view--address {
    border-bottom: 1px solid $primary-light;
    padding-bottom: 15px;
  }
}

.instant-quote-details-view--service-header {
  padding-bottom: 20px;
}

.instant-quote-details-view--details-wrapper {
  border-bottom: 1px solid $primary;
  padding-bottom: 30px;
}
.instant-quote-details-view--details {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid $primary-light;
  }

  @media (max-width: $mobile-max-width) {
    flex-direction: column;
  }
}

.instant-quote-details-view--detail {
  padding-left: 4px;
  text-align: right;

  @media (max-width: $mobile-max-width) {
    text-align: left;
    padding-left: 0px;
  }
}

.ls-left-column {
  padding-right: 30px;
  @media (max-width: $mobile-max-width) {
    padding-right: 10px;
  }
}

.ls-right-column {
  padding-left: 30px;

  @media (max-width: $mobile-max-width) {
    padding-left: 10px;
  }
}

.instant-quote-details-view--recurring-detail > * {
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid $primary-light;
}

.instant-quote-details-view--photos-text,
.instant-quote-details-view--additional-text {
  padding: 10px 0 10px 0;
  font-weight: bold;
}

.instant-quote-details-view--additional {
  padding-bottom: 30px;
}

.instant-quote-details-view--additional-details {
  white-space: pre-wrap;
  font-style: italic;
}

.instant-quote-details-view--images {
  display: flex;
}

.instant-quote-details-view--images-wrap {
  display: flex;
  flex-wrap: wrap;
}

.instant-quote-details-view--image {
  margin-right: 4px;
}

.instant-quote-details-view--price {
  padding-top: 30px;
}

.insant-quote-details-view--history-header {
  margin-bottom: 30px;
  @media (max-width: $mobile-max-width) {
    padding: 10px 0;
    border-bottom: 1px solid $primary-light;
    margin-bottom: 10px;
  }
}
.themed-app {
  @media (max-width: $mobile-max-width) {
    .instant-quote-details-view--address {
      border-color: $theme-color-primary-light;
    }
  }
  .instant-quote-details-view--details-wrapper {
    border-color: $theme-color-primary;
  }
  .instant-quote-details-view--details {
    &:not(:last-child) {
      border-color: $theme-color-primary-light;
    }
  }
  .instant-quote-details-view--recurring-detail > * {
    border-color: $theme-color-primary-light;
  }
  .insant-quote-details-view--history-header {
    @media (max-width: $mobile-max-width) {
      border-color: $theme-color-primary-light;
    }
  }
}
</style>
